/**
 * Todo Comments Section Component
 * Displays comments list and input
 * Supports promotion pattern with SecondaryPanelContent
 *
 * Compose is plain text today (API wraps via plainTextToBasecampHtml).
 * Lexxy-parity TipTap is a later bet; until then offer Open in Basecamp (+ copy draft).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { BasecampTodo } from '@/types/basecamp';
import { SecondaryPanelContent } from '@/components/layout/secondary-panel-content';
import { BasecampIcon } from '@/components/icons/basecamp-icon';
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons';
import {
  buildBasecampWebUrl,
  normalizeBasecampWebHost,
} from '@/lib/basecamp-external-url';
import { testIds } from '@/lib/test-ids';
import { toast } from 'sonner';
import { fixBasecampCommentHtml } from '../utils/comment-html';
import { useHydrateBasecampImages } from '../hooks/use-hydrate-basecamp-images';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  creator?: {
    name: string;
    avatar_url?: string;
  };
}

interface TodoCommentsSectionProps {
  todo: BasecampTodo;
  comments: Comment[];
  isLoadingComments: boolean;
  projectId?: string;
  onDemote?: () => void;
  onPostComment?: (content: string) => Promise<void>;
  isPostingComment?: boolean;
}

function CommentHtml({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useHydrateBasecampImages(ref, html)
  return (
    <div
      ref={ref}
      className="basecamp-comment-content prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-p:leading-relaxed prose-a:text-primary prose-a:underline prose-a:decoration-primary/50 prose-a:underline-offset-2 hover:prose-a:text-primary/80 prose-strong:font-semibold prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:font-mono prose-code:text-foreground prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto prose-pre:my-3 prose-blockquote:border-l-4 prose-blockquote:border-border prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function resolveTodoWebUrl(todo: BasecampTodo, projectId?: string): string | null {
  const url = todo.web_url || todo.app_url;
  if (url) {
    return normalizeBasecampWebHost(
      url.replace('basecampapi.com', 'basecamp.com').replace('.json', ''),
    );
  }
  if (projectId && todo.id) {
    const accountMatch = String(todo.app_url || '').match(
      /\/\/(?:3|app)\.basecamp(?:api)?\.com\/(\d+)\//,
    );
    const accountId = accountMatch ? accountMatch[1] : '5744549';
    return buildBasecampWebUrl(todo.id, projectId, 'todo', accountId);
  }
  return null;
}

export function TodoCommentsSection({
  todo,
  comments,
  isLoadingComments,
  projectId,
  onDemote,
  onPostComment,
  isPostingComment = false,
}: TodoCommentsSectionProps) {
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState(false);
  const canSubmit = draft.trim().length > 0 && !isPostingComment;
  const basecampUrl = useMemo(
    () => resolveTodoWebUrl(todo, projectId),
    [todo, projectId],
  );
  const OpenExternalIcon = resolveOpenExternalIcon(BasecampIcon);

  // Draft a message ACTION fills this form (user still posts manually).
  useEffect(() => {
    const handleFill = (event: Event) => {
      const detail = (event as CustomEvent<{ todoId?: string; text?: string }>).detail;
      if (!detail?.text) return;
      if (detail.todoId && String(detail.todoId) !== String(todo.id)) return;
      setDraft(detail.text);
      setComposing(true);
    };
    window.addEventListener('basecamp-comment-form-fill', handleFill);
    return () => window.removeEventListener('basecamp-comment-form-fill', handleFill);
  }, [todo.id]);

  // Repair Basecamp's rich-text HTML (mentions nested in <p> get displaced by the
  // browser's HTML parser - see comment-html.ts) before it is ever rendered.
  const fixedContentById = useMemo(() => {
    const map = new Map<string, string>();
    for (const comment of comments) {
      map.set(comment.id, fixBasecampCommentHtml(comment.content));
    }
    return map;
  }, [comments]);

  const handleSubmit = async () => {
    if (!canSubmit || !onPostComment) return;
    try {
      await onPostComment(draft);
      setDraft('');
      setComposing(false);
    } catch {
      // toast handled by caller
    }
  };

  const handleNeverMind = () => {
    if (isPostingComment) return;
    setDraft('');
    setComposing(false);
  };

  const handleCopyDraft = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Draft copied', {
        description: 'Paste into Basecamp Lexxy when you need formatting or mentions.',
      });
    } catch {
      toast.error('Could not copy draft');
    }
  };

  const handleOpenInBasecamp = async () => {
    if (!basecampUrl) {
      toast.error('Could not open Basecamp', {
        description: 'Missing todo URL. Try refreshing the todo.',
      });
      return;
    }
    const text = draft.trim();
    if (text) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Draft copied - opening Basecamp', {
          description: 'Paste into the Lexxy comment field.',
        });
      } catch {
        toast.message('Opening Basecamp', {
          description: 'Copy your draft from Maater if you need it in Lexxy.',
        });
      }
    }
    window.open(basecampUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <SecondaryPanelContent
      title="Comments"
      onClose={onDemote}
      loading={isLoadingComments}
    >
      <div className="p-4 space-y-4">
        {comments.length > 0 ? (
          comments.map((comment, index) => (
            <div
              key={comment.id}
              className="space-y-2"
              style={{
                animation: `slideDownStagger 0.4s ease-out ${index * 0.05}s both`
              }}
            >
              <div className="flex items-start gap-2">
                {comment.creator?.avatar_url ? (
                  <img
                    src={comment.creator.avatar_url}
                    alt={comment.creator.name}
                    className="h-8 w-8 rounded-full flex-shrink-0"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground flex-shrink-0">
                    {(comment.creator?.name || 'U')[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {comment.creator?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(comment.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pl-10 text-sm text-foreground">
                <CommentHtml html={fixedContentById.get(comment.id) ?? comment.content} />
              </div>
            </div>
          ))
        ) : isLoadingComments ? null : (
          <p className="text-sm text-muted-foreground italic text-center py-8">No comments yet</p>
        )}
      </div>

      {/* Comment input stays closed until Reply. Plain text v1; Lexxy paste via Copy. */}
      {isLoadingComments ? null : composing ? (
      <div className="px-4 py-3 border-t border-border bg-card flex-shrink-0 space-y-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type your comment here..."
          disabled={isPostingComment}
          data-testid={testIds.basecamp.todoCommentInput}
          className="w-full min-h-[5rem] px-3 py-2 text-sm bg-muted rounded-md border border-border text-foreground placeholder-muted-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <p className="text-[11px] text-muted-foreground leading-snug">
          Plain text posts from Maater. For Lexxy formatting or @mentions, copy and open in Basecamp.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => { void handleSubmit(); }}
            data-testid={testIds.basecamp.todoCommentSubmit}
            className="rounded-full px-4"
          >
            {isPostingComment ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Posting...
              </>
            ) : (
              'Add this comment'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isPostingComment}
            onClick={handleNeverMind}
            data-testid={testIds.basecamp.todoCommentCancel}
            className="rounded-full px-4 bg-transparent"
          >
            Never mind
          </Button>
          <div className="flex-1 min-w-[0.5rem]" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isPostingComment || !draft.trim()}
                onClick={() => { void handleCopyDraft(); }}
                data-testid={testIds.basecamp.todoCommentCopyDraft}
                className="rounded-full px-3"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copy
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              Copy draft to paste into Basecamp Lexxy
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isPostingComment || !basecampUrl}
                onClick={() => { void handleOpenInBasecamp(); }}
                data-testid={testIds.basecamp.todoCommentOpenBasecamp}
                className="rounded-full px-3"
              >
                <OpenExternalIcon className="h-3.5 w-3.5 mr-1.5" />
                Open in Basecamp
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {draft.trim()
                ? 'Copies draft (if any) and opens this todo in Basecamp to post with Lexxy'
                : 'Open this todo in Basecamp to comment with Lexxy'}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      ) : (
      <div className="px-4 py-3 border-t border-border bg-card flex-shrink-0">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setComposing(true)}
          data-testid={testIds.basecamp.todoCommentReply}
          className="rounded-full px-4"
        >
          Reply
        </Button>
      </div>
      )}
    </SecondaryPanelContent>
  );
}
